import './style.scss'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import utility from 'src/utils/utility'
import AuthApiContext, { AuthApiProvider } from 'src/provider/API/v2/call-service/Auth'
import AppInfoContext from 'src/provider/state-manager/appInfoProvider'
import { AppText, CustomContainer, FlexColumn, FlexRow } from 'src/style'
import { Logo } from 'src/assets/icons'
import access from 'src/utils/localAccess'
import AuthenticationWorld from 'src/assets/v2/img/authentication_world.svg'
import helpers from 'src/utils/helpers'

import { LoginForm, ForgotPasswordForm, NewPasswordForm, EMAIL_REGEX, isValidCode, LoginInput, ResetInput, View } from 'src/componentv2'
import { OTPVerification2 } from 'src/app/preAuth2/forgetPassword2'

const SignInContainer: React.FC = () => {
    const navigate = useNavigate()
    const { login, validateLoginOtp, forgotPassword, resetPassword } = useContext(AuthApiContext)
    const { info } = useContext(AppInfoContext)
    const [isAuth, setIsAuth] = useState<boolean>(!!info.adminAuthToken)

    const [view, setView] = useState<View>('login')
    const [showOtp, setShowOtp] = useState(false)

    const [showLoginOtp, setShowLoginOtp] = useState(false)
    const [loginOtp, setLoginOtp] = useState('')
    const [loginOtpError, setLoginOtpError] = useState(false)
    const [loginMaskedEmail, setLoginMaskedEmail] = useState('')

    const [input, setInput] = useState<LoginInput>({ ibInstitutionCode: '', userID: '', password: '' })
    const [showPassword, setShowPassword] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [incorrectPassword, setIncorrectPassword] = useState(false)

    const [resetInput, setResetInput] = useState<ResetInput>({
        ibInstitutionCode: '', email: '', otp: '',
        newPassword: '', confirmPassword: ''
    })
    const [resetSubmitAttempted, setResetSubmitAttempted] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [otpError, setOtpError] = useState(false)
    const [resetMaskedEmail, setResetMaskedEmail] = useState('')

    async function initializer() {
        access.setNavigationAccess(navigate)
        let authToken = await localStorage.getItem('adminAuthToken')

        if (!authToken) setIsAuth(!!authToken)
        else navigate('dashboard')
    }

    // ---------- login ----------
    const set = (key: keyof LoginInput) => (value: string) => {
        setInput((prev) => ({ ...prev, [key]: value }))
        if (key === 'password' && incorrectPassword) setIncorrectPassword(false)
    }

    const isUserIdInvalid = () => {
        const value = input.userID.trim()
        if (!value) return true
        if (value.includes('@')) return !EMAIL_REGEX.test(value)
        return false
    }

    const errors = {
        ibInstitutionCode: !isValidCode(input.ibInstitutionCode),
        userID: isUserIdInvalid(),
        password: !input.password,
    }

    const fieldError = (key: keyof LoginInput) => {
        if (key === 'password' && incorrectPassword) return 'Incorrect password. Try again'
        if (!submitAttempted || !errors[key]) return undefined
        if (key === 'ibInstitutionCode') return 'Invalid Code'
        if (key === 'userID') return 'Please enter a valid email address/username'
        return undefined
    }

    async function handleLogin(e?: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e?.preventDefault()

        const hasErrors = Object.values(errors).some(Boolean)
        if (hasErrors) {
            setSubmitAttempted(true)
            return
        }

        const response = await login({ ...input })
        if (!response) {
            setIncorrectPassword(true)
            return
        }
        setLoginOtp('')
        setLoginOtpError(false)
        setLoginMaskedEmail(response.maskedEmail || '')
        setShowLoginOtp(true)
    }

    async function handleResendLoginOtp() {
        const response = await login({ ...input })
        if (response) setLoginMaskedEmail(response.maskedEmail || '')
    }

    function setLoginOtpValue(value: string) {
        if (value.length > 6) return
        setLoginOtpError(false)
        setLoginOtp(value)
    }

    async function handleLoginOtpVerification() {
        if (!loginOtp || loginOtp.length !== 6) return

        const response = await validateLoginOtp(input.ibInstitutionCode, loginOtp)
        if (response) {
            setShowLoginOtp(false)
            setLoginOtp('')
            if (Number(input.ibInstitutionCode) === 0) {
                navigate('/dashboardv2/institution-list')
            } else {
                navigate('/dashboardv2')
            }
        } else {
            setLoginOtpError(true)
        }
    }

    // submit as soon as all 6 digits are in, rather than waiting on the Proceed button
    useEffect(() => {
        if (showLoginOtp && loginOtp.length === 6) handleLoginOtpVerification()
    }, [loginOtp])

    // ---------- forgot password: identification ----------
    const setReset = (key: keyof ResetInput) => (value: string) => {
        if (key === 'otp') {
            if (value.length > 6) return
            setOtpError(false)
        }
        setResetInput((prev) => ({ ...prev, [key]: value }))
    }

    const resetIdErrors = {
        ibInstitutionCode: !isValidCode(resetInput.ibInstitutionCode),
        email: !EMAIL_REGEX.test(resetInput.email),
    }

    const resetIdFieldError = (key: 'ibInstitutionCode' | 'email') => {
        if (!resetSubmitAttempted || !resetIdErrors[key]) return undefined
        if (key === 'ibInstitutionCode') return 'Invalid Code'
        return 'Please enter a valid email address'
    }

    async function handleSendOtp() {
        const hasErrors = Object.values(resetIdErrors).some(Boolean)
        if (hasErrors) {
            setResetSubmitAttempted(true)
            return
        }
        const response = await forgotPassword(resetInput.ibInstitutionCode, resetInput.email)
        if (response) {
            setResetMaskedEmail(response.maskedEmail || '')
            setShowOtp(true)
        }
    }

    // handed down to the OTP popup so it never has to own its own API call
    async function handleResendOtp() {
        const response = await forgotPassword(resetInput.ibInstitutionCode, resetInput.email)
        if (response) setResetMaskedEmail(response.maskedEmail || '')
    }

    // ResetPassword validates the OTP together with the new password in one
    // call — there's no standalone "validate OTP" endpoint anymore. So this
    // just holds the OTP (already in resetInput) and moves to the
    // new-password step; a bad OTP only surfaces once that step is submitted.
    function handleOtpEntryComplete() {
        if (resetInput.otp.length !== 6) return
        setShowOtp(false)
        setOtpError(false)
        setView('new-password')
    }

    // submit as soon as all 6 digits are in, rather than waiting on the Proceed button
    useEffect(() => {
        if (showOtp && resetInput.otp.length === 6) handleOtpEntryComplete()
    }, [resetInput.otp])

    // ---------- forgot password: new password ----------
    const passwordsMismatch = !!resetInput.confirmPassword &&
        resetInput.confirmPassword !== resetInput.newPassword

    const newPasswordFieldError = () => {
        if (!resetSubmitAttempted) return undefined
        if (!helpers.validatePassword(resetInput.newPassword)) return 'Password does not meet requirements'
        return undefined
    }

    const confirmPasswordFieldError = () => {
        if (resetSubmitAttempted || resetInput.confirmPassword) {
            if (passwordsMismatch) return "The passwords don't match."
        }
        return undefined
    }

    async function handleResetPassword() {
        if (!helpers.validatePassword(resetInput.newPassword) || passwordsMismatch) {
            setResetSubmitAttempted(true)
            return
        }

        const messageCode = await resetPassword(
            resetInput.ibInstitutionCode, resetInput.otp, resetInput.newPassword, resetInput.confirmPassword
        )

        if (messageCode === 'PASSWORD_UPDATED') {
            setView('login')
            setResetInput({ ibInstitutionCode: '', email: '', otp: '', newPassword: '', confirmPassword: '' })
            setResetSubmitAttempted(false)
            setResetMaskedEmail('')
            return
        }

        // OTP was accepted client-side back at the OTP step but rejected
        // server-side here — send the user back to re-enter it rather than
        // stranding them on the password screen with no OTP field to fix.
        if (messageCode === 'OTP_IS_INVALID') {
            setResetInput((prev) => ({ ...prev, otp: '' }))
            setOtpError(true)
            setShowOtp(true)
        }
    }

    function backToLogin() {
        setView('login')
        setResetSubmitAttempted(false)
        setResetInput({ ibInstitutionCode: '', email: '', otp: '', newPassword: '', confirmPassword: '' })
        setResetMaskedEmail('')
    }

    useEffect(() => {
        initializer()
    }, [])

    return (
        <>
            {!isAuth &&
                <CustomContainer width='100' sizeUnit='%' height='100' hUnit='%'>
                    <FlexRow alignItems='stretch' justifyContent='flex-start' height='100' hUnit='%'>
                        <FlexColumn
                            width="50"
                            sizeUnit="%"
                            minHeight="100"
                            mnHUnit="%"
                            padding="2"
                            bgColor="#ffffff"
                            className="login-about-section"
                            style={{
                                position: "relative",
                                justifyContent: "center",
                                overflow: "hidden",
                                backgroundColor: "#ffffff"
                            }}
                        >
                            <img
                                src={AuthenticationWorld}
                                alt="Authentication"
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover"
                                }}
                            />

                            <div
                                style={{
                                    position: "absolute",
                                    left: "48px",
                                    bottom: "48px",
                                    zIndex: 1
                                }}
                            >
                                <AppText color="#FFFFFF" textSize="2.4" fontWeight="700">
                                    Africa's Foremost
                                </AppText>
                                <AppText color="#FFFFFF" textSize="2.4" fontWeight="700">
                                    Banking-as-a-Platform
                                </AppText>
                            </div>
                        </FlexColumn>

                        <FlexColumn
                            width='50' sizeUnit='%' minHeight='100' mnHUnit='%'
                            bgColor='#ffffff' padding='2'
                            className='login-form-section'
                        >
                            <CustomContainer
                                width='40' leftMargin='auto' rightMargin='auto' sizeUnit=''
                                className='login-form-container'
                            >
                                <Logo className='login-form-logo'/>

                                {view === 'login' &&
                                    <LoginForm
                                        input={input}
                                        set={set}
                                        fieldError={fieldError}
                                        showPassword={showPassword}
                                        setShowPassword={setShowPassword}
                                        handleLogin={handleLogin}
                                        onForgotPassword={() => setView('identification')}
                                    />
                                }

                                {view === 'identification' &&
                                    <ForgotPasswordForm
                                        resetInput={resetInput}
                                        setReset={setReset}
                                        resetIdFieldError={resetIdFieldError}
                                        handleSendOtp={handleSendOtp}
                                        backToLogin={backToLogin}
                                    />
                                }

                                {view === 'new-password' &&
                                    <NewPasswordForm
                                        resetInput={resetInput}
                                        setReset={setReset}
                                        showNewPassword={showNewPassword}
                                        setShowNewPassword={setShowNewPassword}
                                        showConfirmPassword={showConfirmPassword}
                                        setShowConfirmPassword={setShowConfirmPassword}
                                        newPasswordFieldError={newPasswordFieldError}
                                        confirmPasswordFieldError={confirmPasswordFieldError}
                                        handleResetPassword={handleResetPassword}
                                    />
                                }
                            </CustomContainer>
                        </FlexColumn>
                    </FlexRow>
                </CustomContainer>
            }

            {showOtp &&
                <OTPVerification2
                    close={() => setShowOtp(false)}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => setReset('otp')(e.target.value)}
                    formData={resetInput}
                    handleNext={handleOtpEntryComplete}
                    handleResend={handleResendOtp}
                    error={otpError}
                    maskedEmail={resetMaskedEmail}
                />
            }

            {showLoginOtp &&
                <OTPVerification2
                    close={() => { setShowLoginOtp(false); setLoginOtpError(false) }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => setLoginOtpValue(e.target.value)}
                    formData={{ ibInstitutionCode: input.ibInstitutionCode, email: '', otp: loginOtp, newPassword: '', confirmPassword: '' }}
                    handleNext={handleLoginOtpVerification}
                    handleResend={handleResendLoginOtp}
                    error={loginOtpError}
                    maskedEmail={loginMaskedEmail}
                />
            }
        </>
    )
}

const SignIn2 = () => (
    <AuthApiProvider>
        <SignInContainer />
    </AuthApiProvider>
)

export default utility.routeData('/signin2', 'SignIn2', <SignIn2 />)